import {
  type ErrorResult,
  type Result,
  type SuccessResult,
} from '../../../../javascripts/shared-types';
import { type JiraIssueCreateMetaResponse } from '../types';
import { OMIT_FIELDS } from './constants';

// this represents what we send to Jira as a simple field value
type Field = unknown;
// this is used for fields that don't support update
type Update = {
  add: unknown;
}[];

type Fields = Record<string, Field>;
type Updates = Record<string, Update>;

// represents what we send to Jira as {"issueUpdates": [...]}
export type IssueUpdate = {
  fields: Fields;
  update?: Updates;
};

type FieldUpdate =
  | { type: 'fields'; value: Field }
  | { type: 'update'; value: Update };

/** Given a field definition, converts a raw value into the format expected by the Jira API */
export const makeFieldUpdate = (
  field: JiraIssueCreateMetaResponse['fields'][0],
  value: unknown
): Result<[string, FieldUpdate] | undefined> => {
  if (field.required && !value) {
    return {
      status: 'error',
      error: `Field ${field.name} is required`,
    };
  }

  // if the provided value is nullish, assume we don't want to send it
  if (value === undefined || value === null || value === '') {
    return {
      status: 'success',
      data: undefined,
    };
  }

  const makeIssueLinkUpdate = (values: unknown[]): Update =>
    values.map(value => ({
      add: {
        // TODO hardcoding "blocks" for now, to support this properly we need another dropdown for link type from
        // https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issue-link-types/#api-rest-api-3-issuelinktype-get
        type: {
          name: 'Blocks',
          inward: 'is blocked by',
          outward: 'blocks',
        },
        outwardIssue: {
          id: String(value),
        },
      },
    }));

  const makeUpdate = (): FieldUpdate | undefined => {
    if (field.fieldId === 'description') {
      return {
        type: 'fields',
        value: {
          version: 1,
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: value,
                },
              ],
            },
          ],
        },
      };
    }

    if (field.fieldId === 'parent') {
      return {
        type: 'fields',
        value: { id: value },
      };
    }

    switch (field.schema.type) {
      case 'string':
      case 'any':
        return { type: 'fields', value };
      case 'array': {
        const values = Array.isArray(value) ? value : [value];
        if (field.schema.items === 'string') {
          return { type: 'fields', value: values.map(v => String(v)) };
        } else if (field.schema.items === 'issuelinks') {
          return {
            type: 'update',
            value: makeIssueLinkUpdate(values),
          };
        } else {
          // Assume an id is all that's needed here
          return {
            type: 'fields',
            value: values.map(v => ({ id: String(v) })),
          };
        }
      }

      case 'priority':
      case 'option':
      case 'option-with-child': // TODO handle nested list
        return { type: 'fields', value: { id: value } };
      case 'user':
        return { type: 'fields', value: { accountId: value } };
      case 'date':
        // assume this is a string of the format 'YYYY-MM-DD'
        return { type: 'fields', value };
      case 'number':
        return { type: 'fields', value: Number(value) };

      default:
        console.error(`Unknown schema type`, field, value);

        if (process.env.NODE_ENV !== 'production') {
          throw new Error(`Unknown schema type`);
        }
    }
  };

  const fieldUpdate = makeUpdate();

  return {
    status: 'success',
    data: fieldUpdate ? [field.fieldId, fieldUpdate] : undefined,
  };
};

const isErrorResult = (result: Result<unknown>): result is ErrorResult =>
  result.status === 'error';

const isSuccessResult = <T>(result: Result<T>): result is SuccessResult<T> =>
  result.status === 'success';

/** Converts an array of field definitions and values captured in a form into a FieldUpdates
 * object which can be sent to Jira when creating an issue
 */
export const makeIssueUpdate = (
  fields: JiraIssueCreateMetaResponse['fields'],
  values: Record<string, unknown>
): Result<IssueUpdate> => {
  const results = fields
    .filter(field => !OMIT_FIELDS.includes(field.fieldId))
    .map(field => makeFieldUpdate(field, values[field.fieldId]));

  const errors = results.filter(isErrorResult);

  if (errors.length) {
    return {
      status: 'error',
      error: errors.map(error => error.error).join(', '),
    };
  }

  const data = results
    .filter(isSuccessResult)
    .map(result => result.data)
    .filter(Boolean)
    .reduce<IssueUpdate>(
      (acc, [fieldId, update]) => {
        if (update.type === 'fields') {
          acc.fields[fieldId] = update.value;
        } else {
          if (!acc.update) {
            acc.update = {};
          }
          acc.update[fieldId] = update.value;
        }
        return acc;
      },
      { fields: {} }
    );

  return {
    status: 'success',
    data,
  };
};
